import React, { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { X, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Two-step delete-account confirmation dialog.
 *
 *   Step 1: short explainer + "Continue".
 *   Step 2: re-authentication —
 *           • For password (local) accounts: type your password.
 *           • For OAuth accounts: type literal "DELETE" to confirm.
 *
 * The backend (`DELETE /api/users/me`) re-checks the password / confirm string
 * before erasing the account so a stolen JWT alone cannot delete the user.
 */
export default function DeleteAccountDialog({ user, onClose, onDeleted }) {
  const { lang } = useI18n();
  const isLocalAuth = (user?.auth_provider || "password") === "password";
  const [step, setStep] = useState(1);
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tx = (da, en) => (lang === "da" ? da : en);

  const submit = async (e) => {
    e?.preventDefault();
    if (isLocalAuth && !value) {
      toast.error(tx("Indtast din adgangskode", "Enter your password"));
      return;
    }
    if (!isLocalAuth && value !== "DELETE") {
      toast.error(tx('Skriv "DELETE" for at bekræfte', 'Type "DELETE" to confirm'));
      return;
    }
    setSubmitting(true);
    try {
      const body = isLocalAuth ? { password: value } : { confirm: "DELETE" };
      await api.delete("/users/me", { data: body });
      onDeleted?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error(tx("Forkert adgangskode", "Incorrect password"));
      } else if (status === 400) {
        toast.error(detail || tx("Bekræftelse kræves", "Confirmation required"));
      } else {
        toast.error(detail || tx("Noget gik galt", "Something went wrong"));
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
      data-testid="delete-account-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose?.(); }}
    >
      <div className="relative w-full max-w-md border border-[#262626] bg-[#0A0A0A] p-6">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          data-testid="delete-account-close"
          aria-label={tx("Luk", "Close")}
          className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-white disabled:opacity-50"
        >
          <X weight="bold" size={18} />
        </button>

        <div className="label-overline" style={{ color: "#FF3B30" }}>
          {tx("Slet konto", "Delete account")}
        </div>
        <h3
          id="delete-account-title"
          className="font-display text-2xl tracking-tight mt-2 flex items-center gap-2"
        >
          <Warning weight="duotone" size={22} color="#FF3B30" />
          {tx("Er du helt sikker?", "Are you absolutely sure?")}
        </h3>

        {step === 1 ? (
          <>
            <ul className="mt-4 space-y-2 text-sm text-zinc-300 list-disc pl-5">
              <li>{tx("Din profil og dine billeder slettes.", "Your profile and avatar are deleted.")}</li>
              <li>{tx("Sessioner du er vært for, og deres beskeder, fjernes.", "Sessions you host (and their chats) are removed.")}</li>
              <li>{tx("Du fjernes fra sessioner du har tilmeldt dig.", "You're removed from sessions you joined.")}</li>
              <li>{tx("Dine vurderinger slettes.", "Your ratings are erased.")}</li>
              <li className="text-zinc-500">{tx("Betalingskvitteringer beholdes anonymt af bogføringsmæssige årsager.", "Payment receipts are kept anonymously for accounting reasons.")}</li>
            </ul>
            <div className="mt-6 flex gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                data-testid="delete-account-cancel"
                className="kinetic-btn border border-[#262626] hover:border-white/40 bg-[#141414] px-4 py-2.5 text-xs uppercase tracking-widest font-bold"
              >
                {tx("Annuller", "Cancel")}
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                data-testid="delete-account-continue"
                className="kinetic-btn border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white px-4 py-2.5 text-xs uppercase tracking-widest font-bold"
              >
                {tx("Fortsæt", "Continue")}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-4">
            <div>
              <label className="label-overline block mb-2">
                {isLocalAuth
                  ? tx("Indtast din adgangskode", "Enter your password")
                  : tx('Skriv "DELETE" for at bekræfte', 'Type "DELETE" to confirm')}
              </label>
              <input
                data-testid="delete-account-input"
                type={isLocalAuth ? "password" : "text"}
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={isLocalAuth ? "••••••••" : "DELETE"}
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF3B30] px-3 py-3 text-sm outline-none font-mono-tag"
              />
              {!isLocalAuth && (
                <p className="text-[11px] text-zinc-500 mt-2">
                  {tx(
                    `Din konto er logget ind via ${user?.auth_provider || "OAuth"}, så vi kræver tekstbekræftelse i stedet for adgangskode.`,
                    `Your account uses ${user?.auth_provider || "OAuth"} sign-in, so we require text confirmation instead of a password.`,
                  )}
                </p>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={submitting}
                data-testid="delete-account-back"
                className="kinetic-btn border border-[#262626] hover:border-white/40 bg-[#141414] px-4 py-2.5 text-xs uppercase tracking-widest font-bold disabled:opacity-50"
              >
                {tx("Tilbage", "Back")}
              </button>
              <button
                type="submit"
                disabled={submitting || !value}
                data-testid="delete-account-confirm"
                className="kinetic-btn bg-[#FF3B30] hover:bg-[#d32a20] disabled:opacity-50 text-white px-4 py-2.5 text-xs uppercase tracking-widest font-bold"
              >
                {submitting
                  ? tx("Sletter…", "Deleting…")
                  : tx("Slet konto permanent", "Permanently delete")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
